import { useEffect, useState } from "react";
import { authClient } from "../lib/auth";
import type { Me } from "../lib/useMe";

type Full = {
  id: string; name: string;
  members: { id: string; role: string; userId: string; user: { name: string; email: string } }[];
  invitations: { id: string; email: string; role: string; status: string; expiresAt: string | Date }[];
};

export function TeamPage({ me, onChanged }: { me: Me; onChanged: () => void }) {
  const [orgId, setOrgId] = useState(me.activeOrganizationId || me.organizations[0]?.organizationId || "");
  const [org, setOrg] = useState<Full | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [msg, setMsg] = useState("");
  const [newTeam, setNewTeam] = useState("");
  const myRole = me.organizations.find(o => o.organizationId === orgId)?.role;
  const canManage = myRole === "owner" || myRole === "admin";

  const load = async () => {
    if (!orgId) return;
    const res = await authClient.organization.getFullOrganization({ query: { organizationId: orgId } });
    if (res.data) setOrg(res.data as unknown as Full);
  };
  useEffect(() => { load(); }, [orgId]);

  const inviteLink = (id: string) => `${window.location.origin}/invite/${id}`;
  const copy = async (text: string) => { try { await navigator.clipboard.writeText(text); setMsg("Invite link copied"); } catch { setMsg(text); } };

  const invite = async (e: React.FormEvent) => {
    e.preventDefault(); setMsg("");
    const res = await authClient.organization.inviteMember({ email, role, organizationId: orgId });
    if (res.error) { setMsg(res.error.message || "Could not invite"); return; }
    setEmail("");
    await load();
    if (res.data?.id) await copy(inviteLink(res.data.id));
  };

  return (
    <>
      <h1>Team</h1>
      {me.organizations.length > 1 && (
        <label className="field" style={{ maxWidth: 320 }}>Team
          <select value={orgId} onChange={e => { setOrgId(e.target.value); authClient.organization.setActive({ organizationId: e.target.value }); }}>
            {me.organizations.map(o => <option key={o.organizationId} value={o.organizationId}>{o.name}</option>)}
          </select>
        </label>
      )}
      {!org ? <p className="muted">Loading…</p> : (
        <>
          <div className="card overflow">
            <table className="list">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th /></tr></thead>
              <tbody>
                {org.members.map(m => (
                  <tr key={m.id}>
                    <td>{m.user.name}</td><td className="muted">{m.user.email}</td><td>{m.role}</td>
                    <td style={{ textAlign: "right" }}>
                      {canManage && m.userId !== me.user.id && m.role !== "owner" && (
                        <button className="btn danger" onClick={async () => { await authClient.organization.removeMember({ memberIdOrEmail: m.id, organizationId: orgId }); load(); }}>Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {canManage && (
            <>
              <h2>Invite</h2>
              <form className="card row" onSubmit={invite}>
                <input type="email" required placeholder="teammate@studio.com" value={email} onChange={e => setEmail(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
                <select value={role} onChange={e => setRole(e.target.value as any)}><option value="member">Member</option><option value="admin">Admin</option></select>
                <button className="btn primary">Invite</button>
              </form>
              <p className="muted" style={{ fontSize: 13 }}>Email isn't set up yet, so inviting copies a link for you to send. They sign up with that email and open the link.</p>
              {msg && <p className="ok" style={{ wordBreak: "break-all" }}>{msg}</p>}
              {org.invitations.filter(i => i.status === "pending").length > 0 && (
                <div className="card overflow">
                  <table className="list">
                    <thead><tr><th>Pending invite</th><th>Role</th><th /></tr></thead>
                    <tbody>
                      {org.invitations.filter(i => i.status === "pending").map(i => (
                        <tr key={i.id}>
                          <td>{i.email}</td><td>{i.role}</td>
                          <td style={{ textAlign: "right" }} className="row">
                            <button className="btn" onClick={() => copy(inviteLink(i.id))}>Copy link</button>
                            <button className="btn danger" onClick={async () => { await authClient.organization.cancelInvitation({ invitationId: i.id }); load(); }}>Cancel</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      <h2>New team</h2>
      <form className="card row" onSubmit={async e => {
        e.preventDefault();
        const slug = newTeam.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") + "-" + Math.random().toString(36).slice(2, 6);
        const res = await authClient.organization.create({ name: newTeam, slug });
        if (res.error) { setMsg(res.error.message || "Could not create team"); return; }
        setNewTeam(""); onChanged(); if (res.data?.id) setOrgId(res.data.id);
      }}>
        <input required placeholder="Team name" value={newTeam} onChange={e => setNewTeam(e.target.value)} style={{ flex: 1 }} />
        <button className="btn">Create team</button>
      </form>
    </>
  );
}
